import type { Product } from '@agos/api-client/model'
import { T, formatCount, formatMoney, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { useMemo, useState } from 'react'

import { addonCount, addonGroups } from './addon-groups'

/** The fourteen EU allergens, stored on the product as a comma-separated list. */
export const ALLERGENS = [
  { id: 1, label: 'Lepok (obilniny)' },
  { id: 2, label: tt('Kôrovce') },
  { id: 3, label: 'Vajcia' },
  { id: 4, label: 'Ryby' },
  { id: 5, label: tt('Arašidy') },
  { id: 6, label: tt('Sója') },
  { id: 7, label: 'Mlieko' },
  { id: 8, label: tt('Orechy (vlašské, lieskové...)') },
  { id: 9, label: 'Zeler' },
  { id: 10, label: tt('Horčica') },
  { id: 11, label: tt('Sézam') },
  { id: 12, label: tt('Oxid siričitý / siričitany') },
  { id: 13, label: tt('Vlčí bob') },
  { id: 14, label: tt('Mäkkýše') },
]

export function AllergenPicker({
  value,
  onChange,
}: {
  value: number[]
  onChange: (allergenIds: number[]) => void
}) {
  const toggle = (allergenId: number) =>
    onChange(
      value.includes(allergenId)
        ? value.filter((id) => id !== allergenId)
        : [...value, allergenId].sort((left, right) => left - right),
    )

  const chosen = ALLERGENS.filter((allergen) => value.includes(allergen.id))

  return (
    <div className="space-y-2">
      {/* §9.191 - the chip printed `{allergen.id}` and hid the name in a `title`: fourteen bare
          numbers, and on a touch screen no way at all to learn that 7 is milk. The number stays
          because it is what `products.allergens` stores and what the customer site prints. */}
      <div className="flex flex-wrap gap-1.5">
        {ALLERGENS.map((allergen) => (
          <button
            key={allergen.id}
            type="button"
            onClick={() => toggle(allergen.id)}
            data-allergen={allergen.id}
            aria-pressed={value.includes(allergen.id)}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all',
              value.includes(allergen.id)
                ? 'bg-amber-500/15 text-amber-600 border-amber-500/30'
                : 'bg-muted/40 text-muted-foreground border-transparent hover:border-border',
            )}
          >
            <span className="tabular-nums opacity-70">{allergen.id}</span>
            <span>{allergen.label}</span>
          </button>
        ))}
      </div>
      {/* Three of fourteen lit is still fourteen things to scan. */}
      {chosen.length > 0 && (
        <p className="text-xs text-amber-600 font-medium" data-allergens-chosen={chosen.length}>
          <T>Vybrané alergény</T>: {chosen.map((allergen) => `${allergen.id} ${allergen.label}`).join(', ')}
        </p>
      )}
    </div>
  )
}

/**
 * Which other products may be added to this one at the till.
 *
 * Held locally and written with the product, rather than saved on every click:
 * a half-applied addon set is harder to reason about than one that lands with
 * the rest of the form.
 */
export function AddonSelector({
  productId,
  products,
  selectedIds,
  onChange,
}: {
  productId: number | null
  products: Product[]
  selectedIds: number[]
  onChange: (addonProductIds: number[]) => void
}) {
  const [search, setSearch] = useState('')
  const selected = useMemo(() => new Set(selectedIds), [selectedIds])

  // §9.227 — grouped by category, and **no cap**. This sliced to 60 against 164 active products in
  // 18 categories, so 104 could not be reached unless a name was guessed, with nothing saying so.
  const groups = useMemo(() => addonGroups(products, productId, search), [products, productId, search])
  const offered = addonCount(groups)

  const toggle = (addonProductId: number) =>
    onChange(
      selected.has(addonProductId)
        ? selectedIds.filter((id) => id !== addonProductId)
        : [...selectedIds, addonProductId],
    )

  return (
    <div className="space-y-2">
      <input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={tt('Hľadať produkt...')}
        className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20"
      />
      {/* Legacy's count, so somebody can see at a glance how many are attached - and it is the only
        * thing that says so once the list is scrolled. */}
      <p className="text-xs text-muted-foreground text-right" data-addon-count>
        {formatCount(selectedIds.length)} <T>vybraných</T>
      </p>
      <div className="max-h-48 overflow-y-auto border border-border rounded-xl overflow-hidden">
        {offered === 0 && (
          <p className="text-xs text-muted-foreground text-center py-3">
            <T>Žiadne produkty</T>
          </p>
        )}
        {groups.map((group) => (
          <div key={group.category} data-addon-group={group.category}>
            <div className="px-3 py-1 bg-muted/50 text-xs font-bold text-muted-foreground uppercase tracking-wider">
              <T>{group.category}</T>
            </div>
            {group.products.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => product.id !== undefined && toggle(product.id)}
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left transition-colors border-t border-border/40',
                  product.id !== undefined && selected.has(product.id)
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'hover:bg-muted/30 text-muted-foreground',
                )}
              >
                <span className="truncate flex-1">
                  <T>{product.name}</T>
                </span>
                {/* Legacy shows the price, which is how two similarly named products are told apart. */}
                <span className="shrink-0 tabular-nums">{formatMoney(product.price ?? 0)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
